import React, { useState } from 'react';
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { MapPin, Home, PoundSterling, Maximize, ArrowUp, ArrowDown } from 'lucide-react';
import './PropertyOverlays.css';

// Default overlay configuration
const DEFAULT_OVERLAYS = {
  location: {
    enabled: false,
    value: '',
    fontSize: 'medium',
    font: 'Arial',
    color: '#FFFFFF',
    position: 'south_west',
    textShadow: true,
    background: { enabled: false, color: 'rgba(0,0,0,0.6)', rounded: true },
    label: 'Location',
    icon: MapPin,
    placeholder: 'Chelsea, London SW3',
    order: 0
  },
  title: {
    enabled: false,
    value: '',
    fontSize: 'large',
    font: 'Arial',
    color: '#FFFFFF',
    position: 'south_west',
    textShadow: true,
    background: { enabled: false, color: 'rgba(0,0,0,0.6)', rounded: true },
    label: 'Title',
    icon: Home,
    placeholder: '4 Bed Victorian Home',
    order: 1
  },
  price: {
    enabled: false,
    value: '',
    fontSize: 'large',
    font: 'Arial',
    color: '#FFFFFF',
    position: 'south_west',
    textShadow: true,
    background: { enabled: false, color: 'rgba(0,0,0,0.6)', rounded: true },
    label: 'Price',
    icon: PoundSterling,
    placeholder: '£1,250,000',
    order: 2
  },
  sqft: {
    enabled: false,
    value: '',
    fontSize: 'medium',
    font: 'Arial',
    color: '#FFFFFF',
    position: 'south_west',
    textShadow: true,
    background: { enabled: false, color: 'rgba(0,0,0,0.6)', rounded: true },
    label: 'Size',
    icon: Maximize,
    placeholder: '2,450 sq ft',
    order: 3
  }
};

const SIZE_OPTIONS = [
  { value: 'small', label: 'S' },
  { value: 'medium', label: 'M' },
  { value: 'large', label: 'L' },
  { value: 'xlarge', label: 'XL' },
];

// Full position options including mid positions
const POSITION_OPTIONS = [
  { value: 'north_west', label: 'Top Left' },
  { value: 'north', label: 'Top Center' },
  { value: 'north_east', label: 'Top Right' },
  { value: 'mid_north_west', label: 'Mid-Top Left' },
  { value: 'mid_north', label: 'Mid-Top Center' },
  { value: 'mid_north_east', label: 'Mid-Top Right' },
  { value: 'west', label: 'Middle Left' },
  { value: 'center', label: 'Center' },
  { value: 'east', label: 'Middle Right' },
  { value: 'mid_south_west', label: 'Mid-Bottom Left' },
  { value: 'mid_south', label: 'Mid-Bottom Center' },
  { value: 'mid_south_east', label: 'Mid-Bottom Right' },
  { value: 'south_west', label: 'Bottom Left' },
  { value: 'south', label: 'Bottom Center' },
  { value: 'south_east', label: 'Bottom Right' },
];

const COLOR_PRESETS = ['#FFFFFF', '#000000', '#96C11F'];

const PropertyOverlays = ({ 
  overlays, 
  setOverlays, 
  sharedPosition = 'south_west',
  setSharedPosition = () => {},
  groupBackground = { enabled: true, color: 'rgba(0,0,0,0.6)', rounded: true },
  setGroupBackground = () => {},
}) => {
  const [expandedOverlay, setExpandedOverlay] = useState(null);

  const getOverlayConfig = (key) => {
    const defaults = DEFAULT_OVERLAYS[key];
    const current = overlays[key] || {};
    return { ...defaults, ...current };
  };

  const updateOverlay = (key, field, value) => {
    setOverlays(prev => ({
      ...prev,
      [key]: { ...DEFAULT_OVERLAYS[key], ...prev[key], [field]: value }
    }));
  };

  // Move overlay up/down in order
  const moveOverlay = (key, direction) => {
    const activeKeys = Object.entries(overlays)
      .filter(([_, config]) => config.enabled)
      .sort((a, b) => (getOverlayConfig(a[0]).order || 0) - (getOverlayConfig(b[0]).order || 0))
      .map(([k]) => k);
    
    const currentIndex = activeKeys.indexOf(key);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= activeKeys.length) return;
    
    const swapKey = activeKeys[newIndex];
    const currentOrder = getOverlayConfig(key).order;
    const swapOrder = getOverlayConfig(swapKey).order;
    
    setOverlays(prev => ({
      ...prev,
      [key]: { ...prev[key], order: swapOrder },
      [swapKey]: { ...prev[swapKey], order: currentOrder }
    }));
  };

  // Get enabled overlay keys sorted by order
  const enabledKeys = Object.entries(overlays)
    .filter(([_, c]) => c.enabled)
    .map(([k]) => k)
    .sort((a, b) => (getOverlayConfig(a).order || 0) - (getOverlayConfig(b).order || 0));

  return (
    <div className="overlays-simple">
      {/* Master Settings Row */}
      <div className="master-settings">
        <div className="master-row">
          <div className="master-left">
            <label className="master-label">Position</label>
            <select 
              value={sharedPosition}
              onChange={(e) => setSharedPosition(e.target.value)}
              className="position-select"
            >
              {POSITION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          <div className="master-right">
            <label className="bg-toggle">
              <input 
                type="checkbox" 
                checked={groupBackground.enabled} 
                onChange={(e) => setGroupBackground(prev => ({ ...prev, enabled: e.target.checked }))}
              />
              <span>Background</span>
            </label>
            {groupBackground.enabled && (
              <>
                <input
                  type="color"
                  value={groupBackground.color.startsWith('rgba') ? '#000000' : groupBackground.color}
                  onChange={(e) => setGroupBackground(prev => ({ ...prev, color: e.target.value + 'cc' }))}
                  className="bg-color"
                  title="Background color"
                />
                <button
                  className={`corner-btn ${groupBackground.rounded ? 'active' : ''}`}
                  onClick={() => setGroupBackground(prev => ({ ...prev, rounded: !prev.rounded }))}
                  title={groupBackground.rounded ? 'Rounded corners' : 'Square corners'}
                >
                  {groupBackground.rounded ? '●' : '■'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Overlay Items */}
      <div className="overlay-rows">
        {Object.entries(DEFAULT_OVERLAYS).map(([key, defaults]) => {
          const config = getOverlayConfig(key);
          const Icon = defaults.icon;
          const isExpanded = expandedOverlay === key;
          const orderIndex = enabledKeys.indexOf(key);
          const canMoveUp = config.enabled && orderIndex > 0;
          const canMoveDown = config.enabled && orderIndex < enabledKeys.length - 1 && orderIndex !== -1;
          
          return (
            <div key={key} className={`overlay-row ${config.enabled ? 'on' : ''}`}>
              {/* Main Row */}
              <div className="row-main">
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(val) => updateOverlay(key, 'enabled', val)}
                  data-testid={`overlay-toggle-${key}`}
                  className="row-switch"
                />
                <Icon size={12} className="row-icon" />
                
                {config.enabled ? (
                  <>
                    <Input
                      type="text"
                      placeholder={defaults.placeholder}
                      value={config.value}
                      onChange={(e) => updateOverlay(key, 'value', e.target.value)}
                      className="row-input"
                    />
                    
                    {/* Inline Controls */}
                    <div className="row-controls">
                      <div className="ctrl-sizes">
                        {SIZE_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            className={`sz ${config.fontSize === opt.value ? 'on' : ''}`}
                            onClick={() => updateOverlay(key, 'fontSize', opt.value)}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      
                      <div className="ctrl-colors">
                        {COLOR_PRESETS.map(color => (
                          <button
                            key={color}
                            className={`clr ${config.color === color ? 'on' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => updateOverlay(key, 'color', color)}
                          />
                        ))}
                        <input
                          type="color"
                          value={config.color}
                          onChange={(e) => updateOverlay(key, 'color', e.target.value)}
                          className="clr-pick"
                        />
                      </div>

                      <button
                        className={`ctrl-shadow ${config.textShadow !== false ? 'on' : ''}`}
                        onClick={() => updateOverlay(key, 'textShadow', config.textShadow === false)}
                        title={config.textShadow !== false ? 'Shadow On' : 'Shadow Off'}
                      >
                        S
                      </button>
                      
                      <div className="ctrl-order">
                        <button 
                          className={`ord ${!canMoveUp ? 'off' : ''}`}
                          onClick={() => canMoveUp && moveOverlay(key, 'up')}
                          disabled={!canMoveUp}
                        >
                          <ArrowUp size={11} />
                        </button>
                        <button 
                          className={`ord ${!canMoveDown ? 'off' : ''}`}
                          onClick={() => canMoveDown && moveOverlay(key, 'down')}
                          disabled={!canMoveDown}
                        >
                          <ArrowDown size={11} />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <span className="row-label">{defaults.label}</span>
                )}
              </div>
              
              {/* Expanded Options removed - shadow toggle now inline */}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { DEFAULT_OVERLAYS, SIZE_OPTIONS, POSITION_OPTIONS, COLOR_PRESETS };
export default PropertyOverlays;
